import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Grid, IconButton } from "@mui/material"
import { Menu, MenuItem, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { DeleteModelRequest } from "../../../types/custom/projects"
import type { Model } from "../../../types/generated/api/Model"
import type { PaginatedModelListCreateList } from "../../../types/generated/api/PaginatedModelListCreateList"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { setCurrentModel } from "../Slice"
import type { DropDownMenuProps } from "../interfaces"
import { LogsDialog } from "./LogsDialog"
import { MetaDataDialog } from "./MetaDataDialog"
import { RetrainDialog } from "./RetrainDialog"
import { SwitchDialog } from "./SwitchDialog"

import styles from "../Models.module.scss"

/**
 * Container component for model dropdown menu functionality
 * Provides options for viewing metadata, copying Id, and managing model state
 *
 * @param {Model} model - The model for which the dropdown menu is being displayed
 * @returns {React.ReactElement}
 */
export function DropDownMenu(props: Readonly<DropDownMenuProps>): ReactElement {
  const { model } = props

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { t } = useTranslation("models")

  // Get user permissions
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canSwitchModelState = useIsPermitted("Switch model state", permissions.konan, flattenedKonanPermissions)
  const canDeleteModel = useIsPermitted("Delete model", permissions.konan, flattenedKonanPermissions)

  // State for menu anchor element
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isMetaDataDialogOpen, setIsMetaDataDialogOpen] = useState<boolean>(false)
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)
  const [isLogsDialogOpen, setIsLogsDialogOpen] = useState<boolean>(false)
  const [isRetrainSwitchDialogOpen, setIsRetrainSwitchDialogOpen] = useState<boolean>(false)

  /**
   * State for model deletion dialog
   * @type {{ name: string; id: string; isLive: boolean }}
   */
  const [modelToDelete, setModelToDelete] = useState<{ name: string; id: string; isLive: boolean }>({
    name: "",
    id: "",
    isLive: false,
  })

  const { data: nextModel } = useQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError>(
    ["models", currentProject?.uuid, "state"],
    () =>
      KonanAPI.fetchModels({
        projectId: currentProject?.uuid as string,
        ordering: "state",
      }),
    {
      refetchOnWindowFocus: false,
    },
  )

  const { data: modelData } = useQuery<AxiosResponse<Model>, AxiosError>(
    ["model", model?.uuid],
    () => KonanAPI.fetchModel(model?.uuid),
    {
      refetchOnWindowFocus: false,
    },
  )

  // Mutation for handling model deletion
  // Includes success/error notifications and query invalidation
  const deleteModelMutation = useMutation<AxiosResponse, AxiosError, DeleteModelRequest>({
    mutationFn: KonanAPI.deleteModel,
    onSuccess: async () => {
      NotificationUtils.toast(t("toasts.model_deleted_success"), { severity: "success" })

      // Invalidate affected queries
      await Promise.all([
        queryClient.invalidateQueries("models"),
        queryClient.invalidateQueries(["model", modelToDelete.id]),
        queryClient.invalidateQueries(["overviewModels", currentProject?.uuid]),
      ])

      // Set current model to either the live model or the first available model
      dispatch(setCurrentModel(nextModel?.data?.results?.[0] ?? undefined))

      setIsDeletionDialogOpen(false)

      // Reset mutation state after a delay to prevent alert flickering
      setTimeout(() => deleteModelMutation.reset(), 300)
    },
    onError: () => {
      NotificationUtils.toast(t("toasts.model_deleted_error"), { severity: "error" })
    },
  })

  // Handles the model deletion process
  // Updates queries and manages state after successful deletion
  async function deleteModel(): Promise<void> {
    // Attempt to delete the model
    await deleteModelMutation.mutateAsync({
      model_uuid: modelToDelete?.id,
    })
  }

  /**
   * Handles setting up model for deletion
   * @param {string} name - Model name
   * @param {string} id - Model Id
   * @param {boolean} isLive - Model live status
   */
  const handleModelDeletionClicked = (name: string, id: string, isLive: boolean): void => {
    setModelToDelete({ name, id, isLive })
    setIsDeletionDialogOpen(true)
  }

  return (
    <Fragment>
      {/* Model retraining/switch dialog */}
      {isRetrainSwitchDialogOpen && model?.state === "live" ? (
        <RetrainDialog isOpen setIsOpen={setIsRetrainSwitchDialogOpen} />
      ) : (
        <SwitchDialog isOpen={isRetrainSwitchDialogOpen} setIsOpen={setIsRetrainSwitchDialogOpen} />
      )}

      {/* Model information dialog */}
      {/* Displays metadata about the model including image URL, port, and status */}
      {isMetaDataDialogOpen && (
        <MetaDataDialog isOpen Id={currentModel?.uuid as string} model={model} setClose={setIsMetaDataDialogOpen} />
      )}

      {/* Model Logs Dialog */}
      {isLogsDialogOpen && <LogsDialog isOpen setIsOpen={setIsLogsDialogOpen} />}

      {/* Project deletion dialog */}
      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open
          name={modelToDelete.name}
          mode={"model-deletion"}
          onAccept={deleteModel}
          isLoading={deleteModelMutation.isLoading}
          onClose={() => {
            setIsDeletionDialogOpen(false)
            // Reset mutation state after a delay to prevent alert flickering
            setTimeout(() => deleteModelMutation.reset(), 300)
          }}
        />
      )}

      <Grid item>
        {/* Menu Trigger Button */}
        <IconButton
          size="small"
          aria-label="settings"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          className={styles.headerButton}
          id="model_options"
        >
          <MoreHorizIcon />
        </IconButton>

        {/* Dropdown Menu */}
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          menuMaxContent
        >
          {/* Copy Id Option */}
          <MenuItem
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
              e.stopPropagation()
              navigator.clipboard.writeText(model?.uuid)
              NotificationUtils.toast(t("toasts.copied"), { severity: "success" })
            }}
          >
            <Typography variant="label-bold">{t("menu.copy_uuid")}</Typography>
          </MenuItem>

          {/* View Metadata Option */}
          <MenuItem
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
              e.stopPropagation()
              setAnchorEl(null)
              setIsMetaDataDialogOpen(true)
            }}
          >
            <Typography variant="label-bold">{t("menu.view_metadata")}</Typography>
          </MenuItem>

          {/* Configure Model Option */}
          {currentProject?.type !== "generic" && (
            <MenuItem
              onClick={() => {
                navigate(`/projects/${currentProject?.uuid}/models/configurations?model_id=${currentModel?.uuid}`)
                dispatch(setCurrentModel(currentModel))
              }}
            >
              <Typography variant="label-bold">{t("menu.configure_model")}</Typography>
            </MenuItem>
          )}

          {/* Displays Retrain or Switch buttons based on the model state */}
          {modelData?.data?.pending_action !== "fix-model-schema" && (
            <MenuItem
              onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                e.stopPropagation()
                setAnchorEl(null)
                setIsRetrainSwitchDialogOpen(true)
              }}
            >
              {canSwitchModelState && (
                <Typography variant="label-bold">
                  {model?.state === "live" ? t("menu.retrain_model") : t("menu.switch_model")}
                </Typography>
              )}
            </MenuItem>
          )}

          {/* Dataset Assignment Option */}
          <MenuItem
            onClick={() => {
              navigate(`/projects/${currentProject?.uuid}/data-files?page=Training-data`)
            }}
          >
            <Typography variant="label-bold">{t("menu.assign_to_dataset")}</Typography>
          </MenuItem>

          {/* Data Drift Option */}
          <MenuItem
            onClick={() => {
              dispatch(setCurrentModel(model))
              navigate(`/projects/${currentProject?.uuid}/models/drift`)
            }}
          >
            <Typography variant="label-bold">{t("menu.view_data_drift")}</Typography>
          </MenuItem>

          {/* View Model Logs Option */}
          <MenuItem
            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
              e.stopPropagation()
              setAnchorEl(null)
              setIsLogsDialogOpen(true)
            }}
          >
            <Typography variant="label-bold">{t("menu.view_logs")}</Typography>
          </MenuItem>

          {/* TODO:: fix it using portal in SUI */}
          {/* <Tooltip title={modelState === "live" && "Live models cant be deleted"}> */}
          {/* Delete Model Option - Only shown if user has permission */}
          {canDeleteModel && model && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null)
                handleModelDeletionClicked(model?.name, model?.uuid, model?.state === "live")
              }}
              disabled={model?.state === "live"}
            >
              <Typography variant="label-bold" color="negative" variantColor={model?.state === "live" ? 1 : 2}>
                {t("menu.delete_model")}
              </Typography>
            </MenuItem>
          )}
          {/* </Tooltip> */}
        </Menu>
      </Grid>
    </Fragment>
  )
}
